import { cn } from '@brew/ui/cn'
import Link from 'next/link'
import type { ReactNode } from 'react'

export type PillButtonVariant = 'primary' | 'ghost' | 'ink'

type CommonProps = {
  variant?: PillButtonVariant
  className?: string
  children: ReactNode
}

type LinkProps = CommonProps & {
  href: string
  external?: boolean
  onClick?: never
  type?: never
}

type ButtonProps = CommonProps & {
  href?: never
  external?: never
  onClick: () => void
  type?: 'button' | 'submit'
}

export type PillButtonProps = LinkProps | ButtonProps

const BASE =
  'inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg px-5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground/30'

const VARIANTS: Record<PillButtonVariant, string> = {
  primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
  ghost:
    'border border-border text-foreground hover:border-primary/40 hover:text-primary dark:hover:bg-foreground/10',
  ink: 'bg-foreground text-background hover:bg-foreground/90 dark:bg-foreground dark:text-background',
}

/**
 * Class-string for a PillButton variant. Use when something else needs to
 * adopt the same pill style (e.g. Clerk's `<SignUpButton>` which renders
 * its own `<button>`).
 */
export function pillButtonClasses(
  variant: PillButtonVariant = 'primary',
  className?: string
): string {
  return cn(BASE, VARIANTS[variant], className)
}

/**
 * Marketing pill CTA. Three variants:
 *   - `primary`: brand orange fill, the dominant CTA on a page
 *   - `ghost`: hairline border, foreground text that warms to primary on hover
 *   - `ink`: escape hatch for the legacy dark-ink fill (rare; use `primary` instead)
 */
export function PillButton(props: PillButtonProps) {
  const variant = props.variant ?? 'primary'
  const classes = pillButtonClasses(variant, props.className)

  if ('href' in props && props.href !== undefined) {
    const linkProps = props.external
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {}
    return (
      <Link href={props.href} className={classes} {...linkProps}>
        {props.children}
      </Link>
    )
  }

  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      className={cn('cursor-pointer', classes)}
    >
      {props.children}
    </button>
  )
}
